import React from 'react';
import { ui } from '../../config/uiSizes';

// ── Types ─────────────────────────────────────────────────────────────────────

type StepStatus = 'completed' | 'active' | 'approval' | 'failed' | 'pending';

interface RollbackStep {
    label: string;
    subLabel?: string;
    description: string;
    status: StepStatus;
}

// ── Step derivation ───────────────────────────────────────────────────────────

function buildRollbackSteps(status: string): RollbackStep[] {
    // ── Plan Generation (Job 4: Queued → SetupInProgress → PlanAwaitingApproval) ──
    const planSubLabel =
        status === 'RollbackQueued' ? 'Queued' :
            status === 'RollbackSetupInProgress' ? 'In Progress' : undefined;

    const planStatus: StepStatus =
        status === 'RollbackQueued' || status === 'RollbackSetupInProgress'
            ? 'active'
            : 'completed';

    // ── Review & Approve ──────────────────────────────────────────────────────
    const reviewStatus: StepStatus =
        planStatus !== 'completed' ? 'pending' :
            status === 'RollbackPlanAwaitingApproval' ? 'approval' : 'completed';

    // ── Apply Rollback (Job 5: InProgress → Complete/PartiallyCompleted/Failed) ─
    const applyStatus: StepStatus =
        reviewStatus !== 'completed' ? 'pending' :
            status === 'RollbackInProgress' ? 'active' :
                status === 'RollbackFailed' ? 'failed' : 'completed';

    // ── Complete ──────────────────────────────────────────────────────────────
    const completeSubLabel = status === 'RollbackPartiallyCompleted' ? 'Partial' : undefined;
    const completeStatus: StepStatus =
        applyStatus !== 'completed' ? 'pending' :
            status === 'RollbackComplete' ? 'completed' :
                status === 'RollbackPartiallyCompleted' ? 'active' : 'pending';

    return [
        {
            label: 'Plan',
            ...(planSubLabel !== undefined && { subLabel: planSubLabel }),
            description:
                planStatus === 'active'
                    ? 'Generating the rollback plan'
                    : 'Rollback plan generated',
            status: planStatus,
        },
        {
            label: 'Review',
            description:
                reviewStatus === 'approval' ? 'Awaiting your approval' :
                    reviewStatus === 'completed' ? 'Plan approved' :
                        'Review and approve the plan',
            status: reviewStatus,
        },
        {
            label: 'Apply',
            description:
                applyStatus === 'active' ? 'Applying rollback to target org' :
                    applyStatus === 'failed' ? 'Rollback encountered errors' :
                        applyStatus === 'completed' ? 'Rollback applied successfully' :
                            'Rollback will be applied to target org',
            status: applyStatus,
        },
        {
            label: 'Complete',
            ...(completeSubLabel !== undefined && { subLabel: completeSubLabel }),
            description:
                completeStatus === 'completed' ? 'Rollback completed successfully' :
                    completeStatus === 'active' ? 'Rollback partially completed' :
                        'Rollback will be marked complete',
            status: completeStatus,
        },
    ];
}

// ── Styling maps ──────────────────────────────────────────────────────────────

const stepBg: Record<StepStatus, string> = {
    completed: 'bg-green-500 dark:bg-green-600',
    active: 'bg-blue-500 dark:bg-blue-600',
    approval: 'bg-orange-500 dark:bg-orange-600',
    failed: 'bg-red-500 dark:bg-red-600',
    pending: 'bg-gray-300 dark:bg-gray-600',
};

const stepRing: Record<StepStatus, string> = {
    completed: '',
    active: 'ring-2 ring-offset-2 ring-blue-400 dark:ring-offset-gray-800',
    approval: 'ring-2 ring-offset-2 ring-orange-300 dark:ring-offset-gray-800',
    failed: '',
    pending: '',
};

const stepLabelColor: Record<StepStatus, string> = {
    completed: 'text-green-700 dark:text-green-400',
    active: 'text-blue-700 dark:text-blue-300',
    approval: 'text-orange-600 dark:text-orange-400',
    failed: 'text-red-600 dark:text-red-400',
    pending: 'text-gray-400 dark:text-gray-500',
};

// ── Step icon ─────────────────────────────────────────────────────────────────

const StepIcon: React.FC<{ status: StepStatus; index: number }> = ({ status, index }) => {
    if (status === 'completed') {
        return (
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z"
                    clipRule="evenodd"
                />
            </svg>
        );
    }
    if (status === 'failed') {
        return (
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                />
            </svg>
        );
    }
    if (status === 'approval') {
        return (
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path
                    fillRule="evenodd"
                    d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                    clipRule="evenodd"
                />
            </svg>
        );
    }
    if (status === 'active') {
        return <span className="w-2 h-2 rounded-full bg-white animate-pulse block" />;
    }
    return <span className={`${ui.text.xs} font-bold text-gray-500 dark:text-gray-400`}>{index + 1}</span>;
};

// ── Component ─────────────────────────────────────────────────────────────────

interface RollbackProgressStepperProps {
    status: string;
}

export const RollbackProgressStepper: React.FC<RollbackProgressStepperProps> = ({ status }) => {
    const steps = buildRollbackSteps(status);

    return (
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className={`${ui.text.label} font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3`}>
                Rollback Progress
            </div>
            <div className="relative flex items-start gap-0">
                {steps.map((step, index) => {
                    const connectorColor =
                        step.status === 'completed' ? 'bg-green-400 dark:bg-green-600' :
                            step.status === 'failed' ? 'bg-red-400 dark:bg-red-600' :
                                'bg-gray-300 dark:bg-gray-600';

                    return (
                        <div key={step.label} className="flex-1 flex flex-col items-center relative">
                            {/* Connector line */}
                            {index < steps.length - 1 && (
                                <div
                                    className={`absolute top-3.5 left-1/2 w-full h-0.5 z-0 ${connectorColor}`}
                                />
                            )}

                            {/* Circle */}
                            <div
                                className={`relative z-10 w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${stepBg[step.status]} ${stepRing[step.status]}`}
                            >
                                <StepIcon status={step.status} index={index} />
                            </div>

                            {/* Label + sub-label + description */}
                            <div className="mt-2 text-center px-1">
                                <div className="flex items-center justify-center gap-1 flex-wrap">
                                    <div className={`font-semibold ${ui.text.label} ${stepLabelColor[step.status]}`}>
                                        {step.label}
                                    </div>
                                    {step.subLabel && (
                                        <span
                                            className={`inline-block ${ui.text.label} font-semibold px-1.5 py-0.5 rounded-full leading-none ${step.subLabel === 'Queued'
                                                    ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300'
                                                    : step.subLabel === 'Partial'
                                                        ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                                                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                                                }`}
                                        >
                                            {step.subLabel}
                                        </span>
                                    )}
                                </div>
                                <div className={`${ui.text.label} text-gray-500 dark:text-gray-400 mt-0.5 leading-tight`}>
                                    {step.description}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
